import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authedFetch, daysUntil } from "../lib/api";
import { supabase } from "../lib/supabaseClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import {
  LogOut, ShieldCheck, Clock, CheckCircle2, XCircle, Loader2, MapPin, Mail, Phone, BadgeCheck, AlertTriangle, History, Send, Search, LayoutPanelTop, QrCode, Smartphone,
} from "lucide-react";
import { SRMark } from "../components/SRMark";
import { toast } from "sonner";
import { AdminUsers, AdminGpUsers } from "../components/AdminUsers";
import { AdminAccounts } from "../components/AdminAccounts";
import { AdminNameCorrections } from "../components/AdminNameCorrections";
import { AdminDemos } from "../components/AdminDemos";
import { AdminBulkEmail } from "../components/AdminBulkEmail";
import { AdminInsights } from "../components/AdminInsights";
import { RecoveryEmailManager } from "../components/RecoveryEmailManager";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Australia/Melbourne" }) : "—");

const ADMIN_SECTIONS = [
  ["dashboard", "Dashboard"],
  ["users", "Users"],
  ["accounts", "Subscriptions"],
  ["insights", "Insights"],
  ["demos", "Demos"],
  ["view-site-as", "View site as"],
  ["settings", "Settings"],
];

const withinAdminDate = (value, range) => {
  if (range === "all") return true;
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  if (range === "today") return date.toDateString() === now.toDateString();
  return date >= new Date(now.getTime() - Number(range) * 86400000);
};

const AdminListTools = ({ searchOpen, setSearchOpen, query, setQuery, dateRange, setDateRange, sort, setSort }) => (
  <div className="mt-4 flex flex-wrap items-center gap-2">
    <Button variant="outline" size="icon" onClick={() => setSearchOpen(!searchOpen)} title="Search entries" aria-label="Search entries"><Search className="h-4 w-4" /></Button>
    {searchOpen && <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search names, email or details…" className="h-9 w-64 rounded-md border border-slate-200 bg-white px-3 text-sm" />}
    <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="all">Any date</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option></select>
    <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="name">Name A–Z</option></select>
  </div>
);

const ADMIN_PREVIEW_MODES = [
  { id: "gp", label: "GP", description: "Enter the complete GP interface, including directory search, referral options, account settings and navigation." },
  { id: "psychologist", label: "Psychologist", description: "Enter the complete individual psychologist interface with availability, referrals, Insights, profile, settings and billing." },
  { id: "psychiatrist", label: "Psychiatrist", description: "Enter the complete psychiatrist interface with intake controls, referrals, Insights, profile, settings and billing." },
  { id: "clinic", label: "Clinic manager", description: "Enter the complete clinic interface with team management, availability, referrals, Insights, settings and billing." },
];

const AdminRolePreview = ({ role }) => {
  const [previewTab, setPreviewTab] = useState("dashboard");
  const isManager = role === "practice-manager";
  const isGp = role === "gp";
  const profession = role === "psychiatrist" ? "Psychiatrist" : "Psychologist";
  const previewTabs = isGp ? [["dashboard", "Dashboard"], ["directory", "Clinician directory"], ["tokens", "Referral options"]] : [["dashboard", "Dashboard"], ["directory", "What GPs can see"], ["tokens", "Referral activity"]];
  return (
    <div className="min-h-[700px] bg-slate-50 text-left">
      <div className="flex gap-2 border-b border-slate-200 bg-white px-5 py-3" role="tablist" aria-label="Preview page">
        {previewTabs.map(([id, label]) => (
          <Button key={id} type="button" size="sm" variant={previewTab === id ? "default" : "ghost"} onClick={() => setPreviewTab(id)}>{label}</Button>
        ))}
      </div>
      <div className="border-b border-amber-200 bg-[#FFF7E6] px-5 py-3 text-sm text-amber-950">
        <p className="font-semibold">SimplyRefer is not live yet</p>
        <p className="mt-0.5 text-amber-900">{isGp ? "Live patient referral testing has not started yet. This read-only preview shows the GP directory and referral options." : "Live referrals have not started yet. Complete the profile and explore how SimplyRefer will work; users will be notified before live access begins."}</p>
      </div>
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">SimplyRefer</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{previewTab === "directory" ? (isGp ? "Clinician directory" : "What GPs can see") : previewTab === "tokens" ? (isGp ? "Referral options" : "Referral activity") : (isGp ? "GP dashboard" : isManager ? "Practice manager dashboard" : profession + " dashboard")}</h2>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Preparing for launch</span>
        </div>
      </div>
      <div className="mx-auto max-w-6xl space-y-5 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{isGp ? "Available clinicians" : isManager ? "Clinic readiness" : "Profile readiness"}</p><p className="mt-2 text-2xl font-semibold text-slate-900">{isGp ? "12" : isManager ? "3 of 4 ready" : "82%"}</p><p className="mt-1 text-sm text-slate-500">{isGp ? "Fictional matches in this read-only preview." : isManager ? "One clinician needs to confirm availability." : "Add appointment modes and age groups to finish."}</p></div>
          <div className="rounded-lg border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Availability</p><p className="mt-2 text-lg font-semibold text-emerald-700">Taking patients</p><p className="mt-1 text-sm text-slate-500">Confirmed today · Telehealth and in person</p></div>
          <div className="rounded-lg border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Network value</p><p className="mt-2 text-2xl font-semibold text-slate-900">{isManager ? "28" : "12"}</p><p className="mt-1 text-sm text-slate-500">{isManager ? "Profile views across the clinic" : "GP profile views in the preview period"}</p></div>
        </div>
        <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3"><div><h3 className="font-heading text-lg font-semibold text-slate-900">{isManager ? "Clinician readiness" : "Referral activity preview"}</h3><p className="text-sm text-slate-500">{isManager ? "Manage the roster, AHPRA status and availability freshness." : "Respond without storing patient information."}</p></div><Button size="sm" variant="outline" onClick={() => setPreviewTab("directory")}>{isManager ? "Preview clinician listing" : "Open demo view"}</Button></div>
            {isManager ? (
              <div className="mt-4 overflow-hidden rounded-md border border-slate-200"><div className="grid grid-cols-3 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase text-slate-500"><span>Clinician</span><span>AHPRA</span><span>Availability</span></div>{["Dr Alex Morgan", "Jordan Lee", "Taylor Singh"].map((name, index) => <div key={name} className="grid grid-cols-3 border-t border-slate-100 px-4 py-3 text-sm"><span className="font-medium text-slate-900">{name}</span><span className="text-emerald-700">Verified</span><span className={index === 2 ? "text-amber-700" : "text-slate-600"}>{index === 2 ? "Confirm due" : "Current"}</span></div>)}</div>
            ) : (
              <div className="mt-4 space-y-3">{["Selected for referral by a GP practice", "Profile viewed by a GP practice", "Availability confirmed"].map((event, index) => <div key={event} className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3"><div><p className="font-medium text-slate-900">{event}</p><p className="text-xs text-slate-500">Demonstration activity only</p></div><span className="text-xs text-slate-400">{index === 0 ? "Today" : index + 1 + "d ago"}</span></div>)}</div>
            )}
          </section>
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">What GPs can see</p>
            <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{isGp ? "Clinician listing preview" : isManager ? "Clinic listing preview" : "Your directory listing"}</h3>
            <div className="mt-4 rounded-lg border border-slate-200 p-4"><div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-slate-900">{isManager ? "Example Psychology Clinic" : "Dr Sample Clinician"}</p><p className="text-sm text-slate-500">{isManager ? "Psychologists and psychiatrists" : profession}</p></div><span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">AHPRA verified</span></div><p className="mt-3 text-sm text-slate-600">Anxiety · Trauma · Mood disorders · Telehealth</p><p className="mt-3 text-sm font-medium text-emerald-700">Taking patients · confirmed today</p></div>
            <Button className="mt-4 w-full" variant="outline" onClick={() => setPreviewTab("directory")}>Open full listing preview</Button>
          </section>
        </div>
      </div>
    </div>
  );
};
const AdminLaunchControl = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [working, setWorking] = useState(false);
  const [bannerSettings, setBannerSettings] = useState([]);
  const loadSnapshot = async () => {
    const [response, settingsResponse] = await Promise.all([authedFetch("/admin/launch/preview"), authedFetch("/admin/prelaunch-settings")]);
    if (response.ok) setSnapshot(await response.json());
    if (settingsResponse.ok) setBannerSettings(await settingsResponse.json());
  };
  useEffect(() => { loadSnapshot(); }, []);

  const sendTest = async () => {
    setWorking(true);
    const response = await authedFetch("/admin/launch/test", { method: "POST" });
    setWorking(false);
    toast[response.ok ? "success" : "error"](response.ok ? "Test launch email sent to your Admin address." : "Test launch email could not be sent.");
  };
  const activate = async () => {
    const confirmation = window.prompt(
      `This will bring SimplyRefer live, start ${snapshot?.eligible_trial_count || 0} reserved trial(s), and send private individual launch emails to ${snapshot?.recipient_count || 0} user(s).\n\nType BRING SIMPLYREFER LIVE to continue.`
    );
    if (confirmation !== "BRING SIMPLYREFER LIVE") {
      if (confirmation !== null) toast.error("Launch cancelled — the confirmation phrase did not match.");
      return;
    }
    setWorking(true);
    const response = await authedFetch("/admin/launch/activate", { method: "POST" });
    const result = await response.json().catch(() => ({}));
    setWorking(false);
    if (!response.ok) {
      if (response.status === 403) {
        toast.error(result.detail || "Fresh 2FA verification is required. Open Security & 2FA, verify, then try again.");
      } else {
        toast.error(result.detail || "SimplyRefer was not brought live.");
      }
      return;
    }
    toast.success(`SimplyRefer is live. ${result.trials_started} trial(s) started and ${result.emails_sent} private launch emails sent.`);
    loadSnapshot();
  };

  const bannerEnabled = (scopeType, scopeValue) => {
    const setting = bannerSettings.find((item) => item.scope_type === scopeType && item.scope_value === scopeValue);
    return setting ? setting.enabled : true;
  };
  const toggleBanner = async (scopeType, scopeValue) => {
    const enabled = !bannerEnabled(scopeType, scopeValue);
    const response = await authedFetch("/admin/prelaunch-settings", { method: "POST", body: JSON.stringify({ scope_type: scopeType, scope_value: scopeValue, enabled }) });
    if (!response.ok) return toast.error("Could not update the pre-launch banner setting.");
    const updated = await response.json();
    setBannerSettings((items) => [...items.filter((item) => !(item.scope_type === scopeType && item.scope_value === scopeValue)), updated]);
    toast.success("Pre-launch banner setting updated.");
  };

  return (
    <section className="rounded-xl border border-primary/20 bg-gradient-to-r from-blue-50 to-white p-5 shadow-sm" data-testid="admin-launch-control">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-wide text-primary">Website status</p><h2 className="mt-1 font-heading text-xl font-semibold text-slate-900">{snapshot?.is_live ? "SimplyRefer is live" : "Ready to bring SimplyRefer live?"}</h2><p className="mt-1 text-sm text-slate-600">{snapshot?.is_live ? "The staged-launch banner has switched to the live message. Launch communications are protected against accidental duplicates." : "Preview and test the launch email first. Final activation requires a fresh 2FA session and typed confirmation."}</p>{snapshot && !snapshot.is_live && <p className="mt-3 text-sm font-medium text-slate-800">{snapshot.recipient_count} private email recipient(s) · {snapshot.eligible_trial_count} reserved trial(s) will start</p>}</div>
        <div className="flex flex-wrap gap-2"><Button type="button" variant="outline" onClick={sendTest} disabled={working || snapshot?.is_live}>Send test email</Button><Button type="button" onClick={activate} disabled={working || !snapshot || snapshot.is_live}>{working ? "Working…" : snapshot?.is_live ? "Live" : "Bring SimplyRefer live"}</Button></div>
      </div>
      {!snapshot?.is_live && <div className="mt-5 border-t border-blue-100 pt-4"><p className="text-sm font-semibold text-slate-900">Pre-launch banner visibility</p><p className="mt-1 text-xs text-slate-500">Show or hide the amber “SimplyRefer is not live yet” notice for each audience.</p><div className="mt-3 flex flex-wrap gap-2">{[["global","__all__","All audiences"],["audience","gp","GPs"],["audience","psychologist","Psychologists"],["audience","psychiatrist","Psychiatrists"],["audience","clinic","Practice managers"],["audience","demo","Demo pages"]].map(([scopeType, scopeValue, label]) => <Button key={scopeType + scopeValue} type="button" size="sm" variant={bannerEnabled(scopeType, scopeValue) ? "default" : "outline"} onClick={() => toggleBanner(scopeType, scopeValue)}>{label}: {bannerEnabled(scopeType, scopeValue) ? "On" : "Off"}</Button>)}</div></div>}
    </section>
  );
};


const AdminSecurity = () => {
  const [overview, setOverview] = useState({ users: [], policies: [] });
  const [userListMode, setUserListMode] = useState("regular");
  const [factors, setFactors] = useState([]);
  const [enrolment, setEnrolment] = useState(null);
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [scopeType, setScopeType] = useState("role");
  const [scopeValue, setScopeValue] = useState("psychologist");
  const [required, setRequired] = useState(false);
  const [methods, setMethods] = useState(["totp"]);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [adminEmailVerified, setAdminEmailVerified] = useState(false);

  const loadSecurity = async () => {
    const [{ data }, response] = await Promise.all([supabase.auth.mfa.listFactors(), authedFetch("/admin/security")]);
    setFactors((data?.all || []).filter((factor) => factor.status === "verified"));
    if (response.ok) setOverview(await response.json());
  };
  useEffect(() => { loadSecurity(); }, []);

  const beginTotp = async () => {
    const existing = await supabase.auth.mfa.listFactors();
    const unfinished = (existing.data?.all || []).filter((factor) => factor.factor_type === "totp" && factor.status !== "verified");
    await Promise.all(unfinished.map((factor) => supabase.auth.mfa.unenroll({ factorId: factor.id }).catch(() => null)));
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "SimplyRefer authenticator" });
    if (error) return toast.error(error.message);
    if (!data?.id || !data?.totp?.secret) return toast.error("Authenticator setup could not be started. Please try again.");
    setEnrolment({ type: "totp", factorId: data.id, qr: data.totp.qr_code && data.totp.qr_code.trim().startsWith("<") ? `data:image/svg+xml;utf8,${encodeURIComponent(data.totp.qr_code)}` : data.totp.qr_code, secret: data.totp.secret });
  };
  const beginPhone = async () => {
    if (factors.filter((factor) => factor.factor_type === "phone").length >= 2) return toast.error("You can keep up to two verified mobile numbers.");
    if (!phone.trim()) return toast.error("Enter the mobile number including country code.");
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: "phone", phone: phone.trim(), friendlyName: "SimplyRefer SMS" });
    if (error) return toast.error(error.message);
    const challenge = await supabase.auth.mfa.challenge({ factorId: data.id });
    if (challenge.error) return toast.error(challenge.error.message);
    setEnrolment({ type: "phone", factorId: data.id, challengeId: challenge.data.id });
    toast.success("Verification text sent.");
  };
  const verifyEnrolment = async () => {
    let result;
    if (enrolment.type === "totp") {
      result = await supabase.auth.mfa.challengeAndVerify({ factorId: enrolment.factorId, code });
    } else {
      result = await supabase.auth.mfa.verify({ factorId: enrolment.factorId, challengeId: enrolment.challengeId, code });
    }
    if (result.error) return toast.error(result.error.message);
    toast.success("Two-factor authentication is active.");
    setEnrolment(null); setCode(""); loadSecurity();
  };
  const removeOwnFactor = async (id) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
    if (error) return toast.error(error.message);
    toast.success("Authenticator reset. It can now be enrolled again.");
    loadSecurity();
  };
  const resetUserFactor = async (person, factor) => {
    if (!window.confirm(`Reset ${factor.factor_type === "phone" ? "text-message" : "authenticator"} 2FA for ${person.email}? They will be able to enrol again at their next sign-in.`)) return;
    const response = await authedFetch(`/admin/security/users/${person.id}/factors/${factor.id}`, { method: "DELETE" });
    if (!response.ok) return toast.error("Could not reset this factor.");
    toast.success("2FA factor reset. The user can enrol again.");
    loadSecurity();
  };

  const toggleMethod = (method) => setMethods((current) => current.includes(method) ? current.filter((item) => item !== method) : [...current, method]);
  const savePolicy = async () => {
    setSavingPolicy(true);
    const response = await authedFetch("/admin/security/policy", { method: "POST", body: JSON.stringify({ scope_type: scopeType, scope_value: scopeValue, required, methods }) });
    setSavingPolicy(false);
    if (!response.ok) return toast.error("Could not save the 2FA policy.");
    toast.success("2FA policy saved. Affected users will be prompted at sign-in.");
    loadSecurity();
  };

  const visibleUsers = overview.users.filter((person) => userListMode === "test" ? person.is_test === true : person.is_test !== true);

  return (
    <section data-testid="admin-security-section">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold text-slate-900"><ShieldCheck className="h-6 w-6 text-primary" /> Security &amp; two-factor authentication</h1>
      <p className="mt-1 text-sm text-slate-500">Admin 2FA is mandatory. Psychologists, psychiatrists and practice managers can be required to use 2FA. GP accounts are never required to use it.</p>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Your Admin 2FA</h2>
          {factors.length ? <div className="mt-3 space-y-2">{factors.map((factor) => <div key={factor.id} className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"><span className="font-medium text-emerald-800">{factor.factor_type === "phone" ? "SMS" : "Authenticator app"} enrolled</span><Button size="sm" variant="outline" onClick={() => removeOwnFactor(factor.id)}>Reset</Button></div>)}</div> : <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">No second factor is enrolled. Set one up now; the next Admin login will require it.</p>}
          {!enrolment && <div className="mt-4 space-y-3">{!factors.some((factor) => factor.factor_type === "totp") && <Button type="button" onClick={beginTotp}><QrCode className="mr-2 h-4 w-4" /> Set up Microsoft Authenticator</Button>}{factors.filter((factor) => factor.factor_type === "phone").length < 2 && <div className="flex gap-2"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4…" /><Button type="button" variant="outline" onClick={beginPhone}><Smartphone className="mr-2 h-4 w-4" /> Add mobile number</Button></div>}{adminEmailVerified && <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-700">Email-code 2FA verified for this protected session.</p>}<RecoveryEmailManager onSessionVerified={() => setAdminEmailVerified(true)} /><p className="text-xs text-slate-500">Microsoft Authenticator is recommended. You may also keep up to two verified mobile numbers and two verified email addresses. Email verification lasts 12 hours for protected Admin actions.</p></div>}
          {enrolment && <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4">{enrolment.qr && <><p className="mb-2 text-sm text-blue-900">Scan with Microsoft Authenticator, Google Authenticator, or another standard TOTP app.</p><img src={enrolment.qr} alt="Authenticator QR code" className="mx-auto h-44 w-44 rounded bg-white p-2" /><p className="mt-2 text-center text-xs font-medium text-slate-600">Manual setup key</p><p className="mt-1 break-all text-center font-mono text-xs text-slate-500">{enrolment.secret}</p></>}<Label htmlFor="mfa-code">6-digit verification code</Label><div className="mt-1 flex gap-2"><Input id="mfa-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" /><Button type="button" onClick={verifyEnrolment} disabled={code.length !== 6}>Verify</Button></div></div>}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="font-heading text-lg font-semibold text-slate-900">User enforcement</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-600">Apply to<select value={scopeType} onChange={(e) => { setScopeType(e.target.value); setScopeValue(e.target.value === "global" ? "__all__" : e.target.value === "role" ? "psychologist" : overview.users.find((person) => person.user_metadata?.role !== "gp")?.id || ""); }} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3"><option value="global">All clinician accounts</option><option value="role">A role</option><option value="user">One clinician user</option></select></label>
            <label className="text-sm text-slate-600">Selection{scopeType === "role" ? <select value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3"><option value="clinician">All clinicians</option><option value="psychologist">Psychologists</option><option value="psychiatrist">Psychiatrists</option><option value="clinic">Practice managers</option></select> : scopeType === "user" ? <select value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3">{overview.users.filter((person) => person.user_metadata?.role !== "gp").map((person) => <option key={person.id} value={person.id}>{person.user_metadata?.first_name || person.email} — {person.user_metadata?.role || "clinician"}</option>)}</select> : <div className="mt-1 flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm">All clinician accounts</div>}</label>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-800"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Require 2FA at login</label>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">{[["totp","Authenticator"],["phone","Text message"],["email","Email code"]].map(([id,label]) => <label key={id} className="flex items-center gap-2"><input type="checkbox" checked={methods.includes(id)} onChange={() => toggleMethod(id)} /> {label}</label>)}</div>
          <Button className="mt-4" onClick={savePolicy} disabled={savingPolicy || (required && methods.length === 0)}>{savingPolicy ? "Saving…" : "Save enforcement"}</Button>
          <div className="mt-5 border-t border-slate-100 pt-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current policies</p>{overview.policies.length ? overview.policies.map((policy) => <p key={policy.id || policy.scope_type + policy.scope_value} className="mt-2 text-sm text-slate-600">{policy.scope_type}: <strong>{policy.scope_value}</strong> — {policy.required ? "Required" : "Optional"} ({(policy.methods || []).join(", ")})</p>) : <p className="mt-2 text-sm text-slate-500">GP and clinician 2FA is currently optional.</p>}</div>
        </div>
      </div>
      <div className="mt-5 rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3"><div><h2 className="font-heading text-lg font-semibold text-slate-900">User 2FA enrolment</h2><p className="mt-1 text-sm text-slate-500">Searchable provider details are stored in Auth metadata. Reset a lost authenticator or phone factor here without deleting the account.</p></div><div className="flex items-center gap-2"><select aria-label="Show regular or test 2FA accounts" value={userListMode} onChange={(event) => setUserListMode(event.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700"><option value="regular">Regular users</option><option value="test">Test accounts</option></select><span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{visibleUsers.length} users</span></div></div>
        <div className="mt-4 max-h-80 overflow-auto rounded-md border border-slate-200">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-3 py-2">User</th><th className="px-3 py-2">Provider type</th><th className="px-3 py-2">Signed up</th><th className="px-3 py-2">2FA</th></tr></thead>
            <tbody>{visibleUsers.map((person) => {
              const userFactors = person.factors || [];
              const metadata = person.user_metadata || {};
              const providerType = metadata.provider_type || metadata.profession || metadata.role || "GP";
              return <tr key={person.id} className="border-t border-slate-100"><td className="px-3 py-3"><p className="font-medium text-slate-900">{metadata.display_name || [metadata.first_name, metadata.last_name].filter(Boolean).join(" ") || person.email}</p><p className="text-xs text-slate-500">{person.email}</p></td><td className="px-3 py-3 capitalize text-slate-700">{String(providerType).replace(/-/g, " ")}</td><td className="px-3 py-3 text-slate-600">{person.created_at ? fmtDateTime(person.created_at) : "—"}</td><td className="px-3 py-3">{userFactors.length ? <div className="flex flex-wrap gap-2">{userFactors.map((factor) => <Button key={factor.id} type="button" size="sm" variant="outline" onClick={() => resetUserFactor(person, factor)}>Reset {factor.factor_type === "phone" ? "SMS" : "authenticator"}</Button>)}</div> : <span className="text-xs text-slate-500">Not enrolled</span>}</td></tr>;
            })}</tbody>
          </table>
        </div>
      </div>
    </section>
  );
};


const AdminLiveOverview = () => {
  return (
    <section id="view-site-as" className="scroll-mt-28" data-testid="admin-view-site-as">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Preview SimplyRefer as each user type</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">Use the hello@ SimplyRefer Admin account to enter the same full portal structure each user receives. These isolated Admin views never affect customer records, billing, referrals, emails or Insights.</p>
      </div>
      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Secure Admin role view</p>
        <p className="mt-1">A slim Admin control remains visible so you can switch roles or return to Admin. The normal amber pre-launch notice remains visible until you bring SimplyRefer live.</p>
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {ADMIN_PREVIEW_MODES.map((item) => (
          <Link key={item.id} to={`/admin/view-as/${item.id}`} className="group rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md">
            <div className="flex items-start justify-between gap-4">
              <div><p className="font-heading text-lg font-semibold text-slate-900">Enter {item.label} section</p><p className="mt-2 text-sm leading-relaxed text-slate-500">{item.description}</p></div>
              <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-primary">Open full screen</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
const AdminAccountSettings = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const updatePassword = async () => {
    if (password.length < 12) return toast.error("Use at least 12 characters.");
    if (password !== confirmPassword) return toast.error("Passwords do not match.");
    setSavingPassword(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSavingPassword(false);
    if (error) return toast.error(error.message);
    setPassword(""); setConfirmPassword(""); toast.success("Admin password updated.");
  };
  return <section className="rounded-lg border border-slate-200 bg-white p-5"><h2 className="font-heading text-lg font-semibold text-slate-900">Admin account</h2><p className="mt-1 text-sm text-slate-500">Change your password here. Your authenticator, SMS and email-code options stay above.</p><div className="mt-4 grid gap-3 sm:grid-cols-2"><div><label htmlFor="admin-new-password" className="text-sm font-medium text-slate-700">New password</label><Input id="admin-new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></div><div><label htmlFor="admin-confirm-password" className="text-sm font-medium text-slate-700">Confirm new password</label><Input id="admin-confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></div></div><Button type="button" className="mt-4" onClick={updatePassword} disabled={savingPassword}>{savingPassword ? "Updating…" : "Update password"}</Button></section>;
};

const AdminUserHub = () => {
  const [tab, setTab] = useState("gp");
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Users">
        <Button type="button" size="sm" variant={tab === "gp" ? "default" : "outline"} onClick={() => setTab("gp")}>GPs</Button>
        <Button type="button" size="sm" variant={tab === "clinicians" ? "default" : "outline"} onClick={() => setTab("clinicians")}>Psychologists & psychiatrists</Button>
      </div>
      {tab === "gp" ? <AdminGpUsers /> : <AdminUsers />}
    </section>
  );
};


const AdminDashboard = ({ pending, trials, userActivity, accountCounts, onOpen }) => {
  const [nameCorrections, setNameCorrections] = useState([]);
  useEffect(() => {
    authedFetch("/admin/name-corrections").then(async (response) => {
      if (response.ok) setNameCorrections(await response.json());
    });
  }, []);
  const outstandingTotal = pending.length + nameCorrections.length;
    const cards = [
    ["GP users", accountCounts.gps, "Registered GP accounts", "users"],
    ["Psychologists", accountCounts.psychologists, "All real clinician profiles", "users"],
    ["Psychiatrists", accountCounts.psychiatrists, "All real clinician profiles", "users"],
    ["Clinic managers", accountCounts.clinics, "Practice manager accounts", "users"],
    ["Active trials", trials.length, "Current trial accounts", "accounts"],
  ];
return (
    <section className="space-y-7" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">A clear view of SimplyRefer performance and items requiring action.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map(([label, value, detail, destination]) => (
          <button key={label} type="button" disabled={!destination} onClick={() => destination && onOpen(destination)}
            className="rounded-md border border-slate-200 bg-white p-5 text-left shadow-sm disabled:cursor-default">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
            <p className="mt-1 text-xs text-slate-500">{detail}{destination ? " · Open" : ""}</p>
          </button>
        ))}
      </div>
      <section className="rounded-md border border-slate-200 bg-white p-5">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Outstanding items</h2>
          <p className="mt-1 text-sm text-slate-500">Queues stay collapsed until you choose to open them.</p>
          <div className="mt-4 space-y-2">
            <button type="button" onClick={() => onOpen("outstanding")} className="flex w-full items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left hover:bg-slate-50">
              <span><span className="block font-medium text-slate-800">AHPRA verification</span><span className="text-xs text-slate-500">Oldest requests shown first</span></span>
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-800">{pending.length}</span>
            </button>
            <button type="button" onClick={() => onOpen("outstanding")} className="flex w-full items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left hover:bg-slate-50">
              <span className="font-medium text-slate-800">Name corrections</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-700">{nameCorrections.length}</span>
            </button>
          </div>
      </section>
    </section>
  );
};

const AdminAuditTrail = ({ audit }) => (
  <section>
    <h2 className="font-heading text-xl font-semibold text-slate-900">Admin audit trail</h2>
    <p className="mt-1 text-sm text-slate-500">Security, approval, account and launch changes that are useful for troubleshooting.</p>
    <div className="mt-4 overflow-x-auto rounded-md border border-slate-200 bg-white">
      {audit.length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No Admin actions recorded yet.</div> :
        <table className="min-w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Admin</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Details</th></tr></thead><tbody className="divide-y divide-slate-100">{audit.map((item) => <tr key={item.id}><td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateTime(item.created_at)}</td><td className="px-4 py-3 text-slate-600">{item.actor_email}</td><td className="px-4 py-3 text-slate-700">{String(item.action || "").replaceAll("_", " ").toLowerCase()}</td><td className="px-4 py-3 text-slate-600">{item.detail || "—"}</td></tr>)}</tbody></table>}
    </div>
  </section>
);

const AdminNavigationSettings = ({ order, hidden, onOrderChange, onHiddenChange }) => {
  const move = (id, direction) => {
    const index = order.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    onOrderChange(next);
  };
  return (
    <section>
      <h2 className="font-heading text-xl font-semibold text-slate-900">Navigation and pages</h2>
      <p className="mt-1 text-sm text-slate-500">Reorder Admin sections or hide optional sections. Dashboard and Settings remain available so you cannot lock yourself out.</p>
      <div className="mt-5 grid gap-5 xl:grid-cols-[1fr_1.1fr]">
        <div className="space-y-2">
          {order.map((id, index) => {
            const label = ADMIN_SECTIONS.find(([key]) => key === id)?.[1] || id;
            const locked = id === "dashboard" || id === "insights" || id === "settings";
            return <div key={id} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white p-3">
              <input type="checkbox" checked={!hidden.includes(id)} disabled={locked} onChange={() => onHiddenChange(hidden.includes(id) ? hidden.filter((item) => item !== id) : [...hidden, id])} />
              <span className="flex-1 text-sm font-medium text-slate-800">{label}</span>
              <Button type="button" size="sm" variant="outline" disabled={index === 0} onClick={() => move(id, -1)}>↑</Button>
              <Button type="button" size="sm" variant="outline" disabled={index === order.length - 1} onClick={() => move(id, 1)}>↓</Button>
            </div>;
          })}
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Live preview</p>
          <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
            <div className="space-y-1">{order.filter((id) => !hidden.includes(id)).map((id) => <div key={id} className="rounded px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">{ADMIN_SECTIONS.find(([key]) => key === id)?.[1]}</div>)}</div>
          </div>
          <p className="mt-3 text-xs text-slate-500">Changes are saved for this Admin browser immediately.</p>
        </div>
      </div>
    </section>
  );
};

const AdminSettings = ({ order, hidden, onOrderChange, onHiddenChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = ["navigation", "security", "launch", "email", "account"];
  const requestedTab = searchParams.get("tab");
  const [active, setActive] = useState(() => allowedTabs.includes(requestedTab) ? requestedTab : sessionStorage.getItem("sr-admin-settings-tab") || "navigation");
  useEffect(() => {
    if (allowedTabs.includes(requestedTab) && requestedTab !== active) setActive(requestedTab);
  }, [active, requestedTab]);
  const openSettingsTab = (id) => {
    setActive(id);
    sessionStorage.setItem("sr-admin-settings-tab", id);
    setSearchParams(id === "navigation" ? {} : { tab: id }, { replace: true });
  };
  const options = [["navigation", "Navigation & pages"], ["security", "Security & 2FA"], ["launch", "Website launch"], ["email", "Launch email"], ["account", "My account"]];
  return (
    <section className="space-y-5" data-testid="admin-settings">
      <div><h1 className="font-heading text-2xl font-semibold text-slate-900">Settings</h1><p className="mt-1 text-sm text-slate-500">Manage the platform from one place.</p></div>
      <div className="grid gap-6 lg:grid-cols-[210px_minmax(0,1fr)]">
        <aside className="h-fit rounded-md border border-slate-200 bg-white p-2">
          {options.map(([id, label]) => <button key={id} type="button" onClick={() => openSettingsTab(id)} className={"mb-1 w-full rounded-md px-3 py-2.5 text-left text-sm font-medium " + (active === id ? "bg-secondary text-secondary-foreground" : "text-slate-600 hover:bg-slate-50")}>{label}</button>)}
        </aside>
        <div className="min-w-0">
          {active === "navigation" && <AdminNavigationSettings order={order} hidden={hidden} onOrderChange={onOrderChange} onHiddenChange={onHiddenChange} />}
          {active === "security" && <AdminSecurity />}
          {active === "launch" && <AdminLaunchControl />}
            {active === "email" && <AdminBulkEmail />}
          {active === "account" && <AdminAccountSettings />}
        </div>
      </div>
    </section>
  );
};

export default function AdminPortal() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { section: routeSection } = useParams();
  const [pending, setPending] = useState([]);
  const [trials, setTrials] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [accountCounts, setAccountCounts] = useState({
    total: 0,
    gps: 0,
    clinicians: 0,
    clinics: 0,
    psychologists: 0,
    psychiatrists: 0,
    by_profession: {},
  });
  const [activityRole, setActivityRole] = useState("all");
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [acting, setActing] = useState(null);
  const [reminding, setReminding] = useState(null);
  const validSections = [...ADMIN_SECTIONS.map(([id]) => id), "outstanding", "trials"];
  const normalisedRouteSection = validSections.includes(routeSection) ? routeSection : "dashboard";
  const [section, setSection] = useState(normalisedRouteSection);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("oldest");
  const [navOrder, setNavOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sr-admin-nav-order") || "null");
      const available = ADMIN_SECTIONS.map(([id]) => id);
      return Array.isArray(saved) && saved.length
        ? [...saved.filter((id) => available.includes(id)), ...available.filter((id) => !saved.includes(id))]
        : available;
    } catch { return ADMIN_SECTIONS.map(([id]) => id); }
  });
  const [hiddenSections, setHiddenSections] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sr-admin-hidden-sections") || "[]");
      return Array.isArray(saved) ? saved.filter((id) => !["dashboard", "insights", "settings"].includes(id)) : [];
    }
    catch { return []; }
  });
  useEffect(() => { localStorage.setItem("sr-admin-nav-order", JSON.stringify(navOrder)); }, [navOrder]);
  useEffect(() => { localStorage.setItem("sr-admin-hidden-sections", JSON.stringify(hiddenSections)); }, [hiddenSections]);
  useEffect(() => { setSection(normalisedRouteSection); }, [normalisedRouteSection]);

  const openSection = (nextSection) => {
    const next = validSections.includes(nextSection) ? nextSection : "dashboard";
    setSection(next);
    setQuery("");
    setDateRange("all");
    navigate(next === "dashboard" ? "/admin" : `/admin/${next}`);
  };

  const load = async () => {
    const [pRes, tRes, uRes, countsRes] = await Promise.all([
      authedFetch("/admin/pending-clinicians"),
      authedFetch("/admin/trials"),
      authedFetch("/admin/user-activity"),
      authedFetch("/admin/signup-counts"),
    ]);
    if (pRes.status === 403 || tRes.status === 403) { setForbidden(true); setLoading(false); return; }
    setPending(pRes.ok ? await pRes.json() : []);
    setTrials(tRes.ok ? await tRes.json() : []);
    setUserActivity(uRes.ok ? await uRes.json() : []);
    if (countsRes.ok) setAccountCounts(await countsRes.json());
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    load();
    supabase.auth.mfa.listFactors().then(({ data }) => {
      if ((data?.all || []).length === 0) {
        toast.warning("Admin two-factor authentication must be set up before the next sign-in.");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const decide = async (id, action) => {
    setActing(id + action);
    const res = await authedFetch(`/admin/clinicians/${id}/${action}`, { method: "POST" });
    setActing(null);
    if (!res.ok) {
      const failure = await res.json().catch(() => ({}));
      toast.error(failure.detail || "Could not complete the action. The clinician's record was not changed.");
      load();
      return;
    }
    const result = await res.json();
    if (action === "verify") {
      if (result.already_verified && result.approval_email_already_sent) {
        toast.info("Clinician was already verified. Trial dates were unchanged and the approval email had already been sent.");
      } else {
        const trialText = result.trial_started ? " The 90-day trial has started." : "";
        if (result.approval_email_sent) {
          toast.success(`AHPRA verified — approval saved.${trialText} Approval email sent. They'll appear to GPs at launch.`);
        } else {
          toast.warning(`AHPRA verified — approval saved.${trialText} The approval email was not sent; check Resend. They'll appear to GPs at launch.`);
        }
      }
      await load();
      return;
    }
    toast.success("Clinician rejected.");
    setPending((list) => list.filter((c) => c.id !== id));
  };

  const filterRows = (rows, dateField) => {
    const needle = query.trim().toLowerCase();
        return (Array.isArray(rows) ? rows : []).filter((row) => {
      const matches = !needle || JSON.stringify(row).toLowerCase().includes(needle);
      const date = row[dateField] || row.created_at || row.trial_started_at;
      return matches && withinAdminDate(date, dateRange);
    }).sort((a, b) => {
      const ad = new Date(a[dateField] || a.created_at || a.trial_started_at || 0);
      const bd = new Date(b[dateField] || b.created_at || b.trial_started_at || 0);
      if (sort === "oldest") return ad - bd;
      if (sort === "newest") return bd - ad;
      const an = (a.account_name || a.last_name || a.actor_email || "") + (a.first_name || "");
      const bn = (b.account_name || b.last_name || b.actor_email || "") + (b.first_name || "");
      return an.localeCompare(bn);
    });
  };
  const visiblePending = filterRows(pending, "created_at");
  const visibleTrials = filterRows(trials, "trial_started_at");
  const visibleUserActivity = filterRows(userActivity, "created_at").filter((event) => {
    if (activityRole === "all") return true;
    const roleValue = String(event.role || "").toLowerCase();
    const professionValue = String(event.profession || "").toLowerCase();
    if (activityRole === "gp") return roleValue === "gp";
    if (activityRole === "practice-manager") return roleValue === "clinic";
    if (activityRole === "psychiatrist") return professionValue.includes("psychiatrist");
    if (activityRole === "psychologist") return roleValue === "clinician" && !professionValue.includes("psychiatrist");
    return true;
  });
  const listTools = { searchOpen, setSearchOpen, query, setQuery, dateRange, setDateRange, sort, setSort };

  const handleLogout = async () => { await signOut(); navigate("/admin/login", { replace: true }); };

  const runReminders = async () => {
    setReminding("all");
    const res = await authedFetch("/admin/trials/process-reminders", { method: "POST" });
    setReminding(null);
    const d = res.ok ? await res.json() : { sent: 0 };
    toast.success(`${d.sent || 0} due reminder${d.sent === 1 ? "" : "s"} sent.`);
    load();
  };

  if (!authLoading && !user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            <SRMark />
            <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
            <span className="ml-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-secondary-foreground">Admin</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-button"><LogOut className="mr-2 h-4 w-4" /> Log out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 animate-fadein" data-testid="admin-portal-page">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="admin-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : forbidden ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="admin-forbidden">
            <ShieldCheck className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">This account isn't a SimplyRefer admin.</p>
            <Button variant="outline" className="mt-5" onClick={handleLogout}>Sign out</Button>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[210px_minmax(0,1fr)]">
            <aside className="h-fit rounded-md border border-slate-200 bg-white p-2 shadow-sm lg:sticky lg:top-[88px]" aria-label="Admin sections">
              {navOrder.filter((id) => !hiddenSections.includes(id)).map((id) => {
                const label = ADMIN_SECTIONS.find(([key]) => key === id)?.[1] || id;
                return <button key={id} type="button" onClick={() => openSection(id)}
                  className={"mb-1 w-full rounded-md px-3 py-2.5 text-left text-sm font-medium " + (section === id ? "bg-secondary text-secondary-foreground" : "text-slate-600 hover:bg-slate-50")}>{label}</button>;
              })}
            </aside>
            <div className="min-w-0 space-y-8">
              {section === "dashboard" && <AdminDashboard pending={pending} trials={trials} userActivity={userActivity} accountCounts={accountCounts} onOpen={openSection} />}
            {section === "demos" && <AdminDemos />}
            {section === "outstanding" && (<>
            {/* AHPRA verification queue */}
            <section id="verification" className="scroll-mt-28" data-testid="admin-pending-section">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-primary" />
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">AHPRA verification queue</h1>
                <span className="ml-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid="admin-pending-count">{visiblePending.length} pending</span>
              </div>
              <p className="mt-1 text-sm text-slate-500">Look up each clinician's name on the public AHPRA register, then approve to list them for GPs.</p>
              <AdminListTools {...listTools} />

              {visiblePending.length === 0 ? (
                <div className="mt-5 rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="admin-pending-empty">
                  <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-400" />
                  <p className="mt-2 text-sm text-slate-500">All caught up — no clinicians awaiting verification.</p>
                </div>
              ) : (
                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {visiblePending.map((c) => (
                    <div key={c.id} className="rounded-md border border-slate-200 bg-white p-5" data-testid={`admin-pending-${c.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-base font-semibold text-slate-900">{c.first_name} {c.last_name}</h3>
                          <p className="text-sm text-slate-600">{c.profession} • {c.specialty}</p>
                        </div>
                        <a href={`https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx`} target="_blank" rel="noreferrer"
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-primary hover:bg-slate-50" data-testid={`admin-ahpra-link-${c.id}`}>
                          Check register ↗
                        </a>
                      </div>
                      <div className="mt-3 rounded-md bg-slate-50 px-3 py-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">AHPRA registration number</p>
                        <p className="text-sm font-semibold text-red-600" data-testid={`admin-ahpra-name-${c.id}`}>{c.ahpra_number || "—"}</p>
                      </div>
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{c.suburb}{c.state ? ", " + c.state : ""}</p>
                        {c.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</p>}
                        {c.phone && <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</p>}
                        {c.accounts?.account_name && <p className="text-xs text-slate-400">Account: {c.accounts.account_name} ({c.accounts.account_type})</p>}
                        <p className="text-xs text-slate-500">Submitted {fmtDateTime(c.created_at)}</p>
                      </div>
                      <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4">
                        <Button size="sm" disabled={!!acting} onClick={() => decide(c.id, "verify")} data-testid={`admin-verify-${c.id}`}>
                          {acting === c.id + "verify" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve
                        </Button>
                        <Button size="sm" variant="outline" disabled={!!acting} onClick={() => decide(c.id, "reject")} data-testid={`admin-reject-${c.id}`}>
                          {acting === c.id + "reject" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />} Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            </>)}
            {section === "outstanding" && <AdminNameCorrections />}
            {section === "trials" && (<>
            {/* Trials ending follow-up */}
            <section id="trials" className="scroll-mt-28" data-testid="admin-trials-section">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h2 className="font-heading text-xl font-semibold text-slate-900">Free trials — payment follow-up</h2>
              </div>
              <p className="mt-1 text-sm text-slate-500">Trial and complimentary-access periods, with automatic reminders through Resend.</p>
              <AdminListTools {...listTools} />
              {visibleTrials.length > 0 && (
                <Button size="sm" variant="outline" className="mt-3" disabled={!!reminding} onClick={runReminders} data-testid="admin-run-reminders">
                  {reminding === "all" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Run due reminders now (optional)
                </Button>
              )}

              {visibleTrials.length === 0 ? (
                <div className="mt-5 rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="admin-trials-empty">
                  <p className="text-sm text-slate-500">No active trials.</p>
                </div>
              ) : (
                <div className="mt-5 overflow-hidden rounded-md border border-slate-200 bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                      <tr>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Billing email</th>
                        <th className="px-4 py-3 font-medium">Clinicians</th>
                        <th className="px-4 py-3 font-medium">Trial starts</th>
                        <th className="px-4 py-3 font-medium">Trial ends</th>
                        <th className="px-4 py-3 font-medium">Days left</th>
                        <th className="px-4 py-3 font-medium">Reminders</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleTrials.map((a) => {
                        const dleft = daysUntil(a.trial_ends_at);
                        const urgent = dleft !== null && dleft <= 14;
                        const count = Array.isArray(a.clinicians) ? (a.clinicians[0]?.count ?? a.clinicians.length) : 0;
                        return (
                          <tr key={a.id} data-testid={`admin-trial-${a.id}`} className={urgent ? "bg-amber-50/60" : ""}>
                            <td className="px-4 py-3 font-medium text-slate-900">{a.account_name}</td>
                            <td className="px-4 py-3 text-slate-600">{a.billing_email}</td>
                            <td className="px-4 py-3 text-slate-600">{count}</td>
                            <td className="px-4 py-3 text-slate-600">{fmtDate(a.trial_started_at)}</td>
                            <td className="px-4 py-3 text-slate-600">{fmtDate(a.trial_ends_at)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${urgent ? "border-amber-300 bg-amber-100 text-amber-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                                {urgent && <AlertTriangle className="h-3 w-3" />}
                                {dleft === null ? "—" : dleft < 0 ? `${Math.abs(dleft)}d overdue` : `${dleft}d`}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {(a.trial_reminders_sent || []).length === 0 ? (
                                <span className="text-xs text-slate-400">none</span>
                              ) : (
                                <span className="text-xs text-slate-500">{(a.trial_reminders_sent || []).join(", ")}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            </>)}
            {section === "users" && <AdminUserHub />}
            
            {section === "accounts" && <AdminAccounts />}
            {section === "insights" && <AdminInsights />}
            {section === "view-site-as" && <AdminLiveOverview />}
            {section === "settings" && <AdminSettings order={navOrder} hidden={hiddenSections} onOrderChange={setNavOrder} onHiddenChange={setHiddenSections} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
